import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges, ViewChild } from '@angular/core';
import { NumberUtils } from '@modeso/aumico-lib-core-fe';
import {
    IAccountMappingTemplateGroupItemFLI,
    ICashFlowFinancingActivities,
    ICashFlowInvestingActivities,
    ICashFlowOperatingActivities,
    ICashFlowStatement,
    ICashFlowTotal,
} from '@modeso/types__aumico-lib-annualreport-be';
import { FinancialLineItemCashFlowType } from '@modeso/types__aumico-lib-settings-be';
import { UntilDestroy } from '@ngneat/until-destroy';
import Debug from 'debug';

import { CashFlowFinancingActivitiesComponent } from '../../components/cash-flow-statement-components/cash-flow-from-financing-activities/cash-flow-financing-activities.component';
import { CashFlowInvestingActivitiesComponent } from '../../components/cash-flow-statement-components/cash-flow-from-investing-activities/cash-flow-investing-activities.component';
import { CashFlowOperatingActivitiesComponent } from '../../components/cash-flow-statement-components/cash-flow-from-operating-activities/cash-flow-operating-activities.component';
import {
    CashFlowTotalComponent,
    TotalCaptions,
} from '../../components/cash-flow-statement-components/cash-flow-total/cash-flow-total.component';
import { CashFlowTotalFromActivitiesComponent } from '../../components/cash-flow-statement-components/cash-flow-total-from-activities/cash-flow-total-from-activities.component';
import { ICashFlowFLI, ICashFlowInvestingFLI } from '../../interfaces/ICashFlowFLI.interface';
import { ICashFlowFinancingActivitiesComponentInput } from '../../interfaces/ICashFlowFinancingActivitiesComponentInput.interface';
import { ICashFlowInvestingActivitiesComponentInput } from '../../interfaces/ICashFlowInvestingActivitiesComponentInput.interface';
import { ICashFlowOperatingActivitiesComponentInput } from '../../interfaces/ICashFlowOperatingActivitiesComponentInput.interface';
import { ICashFlowTotalComponentInput } from '../../interfaces/ICashFlowTotalComponentInput.interface';
const debug = Debug('aumico:aumico-lib-annualreport-fe:CashFlowStatementPage');
@UntilDestroy()
@Component({
    selector: 'aumico-annualReport-cash-flow-statement-page',
    templateUrl: 'cash-flow-statement.page.html',
    styleUrls: ['cash-flow-statement.page.scss'],
})
export class CashFlowStatementPage implements OnChanges {
    @ViewChild('operatingActivities', { static: true }) operatingActivitiesRef: CashFlowOperatingActivitiesComponent;
    @ViewChild('investingActivities', { static: true }) investingActivitiesRef: CashFlowInvestingActivitiesComponent;
    @ViewChild('financingActivities', { static: true }) financingActivitiesRef: CashFlowFinancingActivitiesComponent;
    @ViewChild('totalFromActivities', { static: true }) totalFromActivitiesRef: CashFlowTotalFromActivitiesComponent;
    @ViewChild('total', { static: true }) totalRef: CashFlowTotalComponent;

    @Input() previousAnnualProfit: number;
    @Input() mappedBalanceFlis: ICashFlowFLI[] | ICashFlowInvestingFLI[];
    @Input() profitOfTheYearFli: IAccountMappingTemplateGroupItemFLI | undefined;
    @Input() currentYearDate: string;
    @Input() previousYearDate: string;
    @Input() currency: string;
    @Input() cashAndCashEquivalentsFli: IAccountMappingTemplateGroupItemFLI | undefined;
    @Input() previousOpeningCashAndCashEquivalents: number | null;
    @Input() previousClosingCashAndCashEquivalents: number | null;
    @Input() reportLocale: string;
    @Input() isSavedClicked = false;
    @Input() previousTotalCaptions: TotalCaptions;
    @Input() operatingActivitiesTitle: string | undefined;
    @Input() financingActivitiesTitle: string | undefined;
    @Input() investingActivitiesTitle: string | undefined;
    // For edit mode
    private currentCashFlowStatement: ICashFlowStatement | undefined;

    @Output() differenceInTotal = new EventEmitter<{ previousTotal: number; currentTotal: number }>();
    @Input() set savedCashFlowStatement(value: ICashFlowStatement | undefined) {
        if (value != null) {
            this.currentCashFlowStatement = value;
            this.savedOperatingActivitiesData = value.operatingActivities;
            this.savedInvestingActivitiesData = value.investingActivities;
            this.savedFinancingActivitiesData = value.financingActivities;
            this.savedTotalData = value.total;
        }
    }
    @Input() autofillApplied: boolean;

    @Input()
    set cashflowFromUploadAutofill(value: ICashFlowStatement) {
        if (value != null) {
            this.currentCashFlowStatement = value;
            this.savedOperatingActivitiesData = value.operatingActivities;
            this.savedInvestingActivitiesData = value.investingActivities;
            this.savedFinancingActivitiesData = value.financingActivities;
            this.savedTotalData = value.total;
        }
    }

    operatingActivitiesData: ICashFlowOperatingActivitiesComponentInput;
    investingActivitiesData: ICashFlowInvestingActivitiesComponentInput;
    financingActivitiesData: ICashFlowFinancingActivitiesComponentInput;
    totalData: ICashFlowTotalComponentInput;

    savedOperatingActivitiesData: ICashFlowOperatingActivities;
    savedInvestingActivitiesData: ICashFlowInvestingActivities;
    savedFinancingActivitiesData: ICashFlowFinancingActivities;
    savedTotalData: ICashFlowTotal;

    operatingActivitiesCurrentTotal: number; 
    operatingActivitiesPreviousTotal: number;
    investingActivitiesCurrentTotal: number;
    investingActivitiesPreviousTotal: number;
    financingActivitiesCurrentTotal: number;
    financingActivitiesPreviousTotal: number;

    // sum of all 3 tables
    currentTotal: number;
    previousTotal: number;

    recalculateFlis: object | null = null;

    @Input() updateFlisAutomatically: boolean;

    @Output() validityChange = new EventEmitter<boolean>();
    @Output() currentCashflowStatementTotal = new EventEmitter<number>();
    @Output() previousCashflowStatementTotal = new EventEmitter<number>();

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['isSavedClicked'] == null && this.mappedBalanceFlis && this.profitOfTheYearFli && this.cashAndCashEquivalentsFli) {
            this.mapOperatingActivitiesData();
            this.mapInvestingActivitiesData();
            this.mapFinancingActivitiesData();
            this.mapTotalData();
            if (this.currentCashFlowStatement != null) {
                this.adjustSavedData();
            }
        }
    }

    // #Operating Activities Region Start

    private mapOperatingActivitiesData(): void {
        this.operatingActivitiesData = {
            title: this.operatingActivitiesTitle,
            flis: this.mappedBalanceFlis.filter(
                (fli) =>
                    fli.fliCashFlow === FinancialLineItemCashFlowType.OPERATIONS ||
                    fli.fliCashFlow === FinancialLineItemCashFlowType.NETWORKING_CAPITAL,
            ),
            profitOfTheYearFli: this.profitOfTheYearFli,
            currentAnnualProfit: this.profitOfTheYearFli.calculatedClosingBalance,
            previousAnnualProfit: this.previousAnnualProfit,
            currentYearDate: this.currentYearDate,
            previousYearDate: this.previousYearDate,
            currency: this.currency,
        };
    }

    // #Operating Activities Region End

    // #Investing Activities Region Start

    private mapInvestingActivitiesData(): void {
        this.investingActivitiesData = {
            title: this.investingActivitiesTitle,
            flis: (this.mappedBalanceFlis as ICashFlowInvestingFLI[]).filter(
                (fli) => fli.fliCashFlow === FinancialLineItemCashFlowType.INVESTING,
            ),
            currentYearDate: this.currentYearDate,
            previousYearDate: this.previousYearDate,
            currency: this.currency,
        };
    }

    // #Investing Activities Region End

    // Financing Activities Region Start

    private mapFinancingActivitiesData(): void {
        this.financingActivitiesData = {
            title: this.financingActivitiesTitle,
            flis: this.mappedBalanceFlis.filter((fli) => fli.fliCashFlow === FinancialLineItemCashFlowType.FINANCING),
            currentYearDate: this.currentYearDate,
            previousYearDate: this.previousYearDate,
            currency: this.currency,
        };
    }

    // #Financing Activities Region End

    // #Total Region Start

    private mapTotalData(): void {
        if (this.cashAndCashEquivalentsFli) {
            this.totalData = {
                currentTotal: this.currentTotal,
                previousTotal: this.previousTotal,
                cashAndCashEquivalentsFli: this.cashAndCashEquivalentsFli,
                openingCashAndCashEquivalents: this.cashAndCashEquivalentsFli.calculatedOpeningBalance,
                closingCashAndCashEquivalents: this.cashAndCashEquivalentsFli.calculatedClosingBalance,
                previousOpeningCashAndCashEquivalents: this.previousOpeningCashAndCashEquivalents,
                previousClosingCashAndCashEquivalents: this.previousClosingCashAndCashEquivalents,
            };
        }
    }

    // #Total Region End

    getCashFlowStatementOutput(version: number): ICashFlowStatement {
        const total = this.totalRef.getTotalFormValue();
        total.total1 = this.totalFromActivitiesRef.getTitle();

        const cashFlowStatementOutput: ICashFlowStatement = {
            operatingActivities: this.operatingActivitiesRef.getOperatingActivitiesFormValue(),
            investingActivities: this.investingActivitiesRef.getInvestingActivitiesFormValue(),
            financingActivities: this.financingActivitiesRef.getFinancingActivitiesFormValue(),
            total,
            version,
        };

        return cashFlowStatementOutput;
    }

    isCashFlowStatementChanged(): boolean {
        return (
            this.operatingActivitiesRef.tableHasChanges ||
            this.investingActivitiesRef.tableHasChanges ||
            this.financingActivitiesRef.tableHasChanges ||
            this.totalRef.tableHasChanges
        );
    }

    onInvestingActivitiesValidityChange(isValid: boolean): void {
        this.validityChange.emit(isValid);
    }

    onOperatingActivitiesTotalChange(totalChange: { currentTotal: number; previousTotal: number }): void {
        this.operatingActivitiesCurrentTotal = totalChange.currentTotal;
        this.operatingActivitiesPreviousTotal = totalChange.previousTotal;
        this.setCurrentCashFlowStatementTotal();
        this.setPreviousCashFlowStatementTotal();
    }

    onInvestingActivitiesTotalChange(totalChange: { currentTotal: number; previousTotal: number }): void {
        this.investingActivitiesCurrentTotal = totalChange.currentTotal;
        this.investingActivitiesPreviousTotal = totalChange.previousTotal;
        this.setCurrentCashFlowStatementTotal();
        this.setPreviousCashFlowStatementTotal();
    }

    onFinancingActivitiesTotalChange(totalChange: { currentTotal: number; previousTotal: number }): void {
        this.financingActivitiesCurrentTotal = totalChange.currentTotal;
        this.financingActivitiesPreviousTotal = totalChange.previousTotal;
        this.setCurrentCashFlowStatementTotal();
        this.setPreviousCashFlowStatementTotal();
    }

    private setCurrentCashFlowStatementTotal(): void {
        this.currentTotal = NumberUtils.sumIgnoringInfiniteNumbers(
            [
                this.operatingActivitiesCurrentTotal,
                this.investingActivitiesCurrentTotal,
                this.financingActivitiesCurrentTotal,
            ],
            0,
        );
        this.currentCashflowStatementTotal.emit(this.currentTotal);
    }

    private setPreviousCashFlowStatementTotal(): void {
        this.previousTotal = NumberUtils.sumIgnoringInfiniteNumbers(
            [
                this.operatingActivitiesPreviousTotal,
                this.investingActivitiesPreviousTotal,
                this.financingActivitiesPreviousTotal,
            ],
            0,
        );
        this.previousCashflowStatementTotal.emit(this.previousTotal);
    }

    // for older annual reports that don't have the type saved
    private adjustSavedData(): void {
        this.savedOperatingActivitiesData.entries.map((entry) => {
            if (!entry.isCustom) {
                entry.type = this.mappedBalanceFlis.find((fli) => fli.id === entry.fliId)?.fliCashFlow;
            }
            return entry;
        });

        this.savedInvestingActivitiesData.entries.map((entry) => {
            if (!entry.isCustom && entry.staticLabel) {
                entry.type = this.mappedBalanceFlis.find((fli) => fli.id === entry.fliId)?.fliCashFlow;
            }
            return entry;
        });

        this.savedFinancingActivitiesData.entries.map((entry) => {
            if (!entry.isCustom) {
                entry.type = this.mappedBalanceFlis.find((fli) => fli.id === entry.fliId)?.fliCashFlow;
            }
            return entry;
        });
    }

    onDifferenceInTotal($event: { previousTotal: number; currentTotal: number }): void {
        this.differenceInTotal.emit($event);
    }
}
